import { AxiosRequestConfig } from "axios";
import {
  GarageRegisterAnnouncement,
  GarageRegisterAnnouncementCreateDto,
  GarageRegisterAnnouncementUpdateDto,
  GarageRegisterAnnouncementDto,
} from "@/lib/types/garage-register/announcements";
import { BaseService } from "../base";
import { GarageRegisterAnnouncementMapper } from "@/lib/adapters/garage-register/announcement";

// Create base service for API DTOs
const baseGarageRegisterAnnouncementService = new BaseService<
  GarageRegisterAnnouncementDto,
  Omit<GarageRegisterAnnouncementDto, "id" | "created_at" | "updated_at" | "created_by" | "garage_register">,
  Partial<Omit<GarageRegisterAnnouncementDto, "id" | "created_at" | "updated_at" | "created_by" | "garage_register">>
>("/garage-register/announcements/", {
  enableDelete: true,
});

export interface AnnouncementQueryParams {
  garage_register_id?: string | number;
  [key: string]: string | number | boolean | undefined;
}

// Extended service with mapping and garage_register_id support
export const garageRegisterAnnouncementsService = {
  getAll: async (
    params: AnnouncementQueryParams = {},
    config?: AxiosRequestConfig
  ): Promise<GarageRegisterAnnouncement[]> => {
    // Merge params from config with params argument
    const queryParams = {
      ...params,
      ...(config?.params || {}),
    };
    
    // Create config with merged params
    const finalConfig: AxiosRequestConfig = config
      ? {
          ...config,
          params: queryParams,
        }
      : {
          params: queryParams,
        };
    
    const response = await baseGarageRegisterAnnouncementService.getAll(
      queryParams,
      finalConfig
    );
    
    // Map API response to frontend format
    return Array.isArray(response)
      ? response.map(GarageRegisterAnnouncementMapper.fromResponse)
      : [];
  },

  getById: async (
    id: string,
    config?: AxiosRequestConfig
  ): Promise<GarageRegisterAnnouncement> => {
    // Ensure garage_register_id is in config params if provided
    const getByIdConfig: AxiosRequestConfig | undefined = config?.params?.garage_register_id
      ? {
          ...config,
          params: {
            garage_register_id: config.params.garage_register_id,
            ...config.params,
          },
        }
      : config;

    const response = await baseGarageRegisterAnnouncementService.getById(
      id,
      getByIdConfig
    );
    return GarageRegisterAnnouncementMapper.fromResponse(response);
  },

  create: async (
    data: GarageRegisterAnnouncementCreateDto,
    config?: AxiosRequestConfig
  ): Promise<GarageRegisterAnnouncement> => {
    // Extract garage_register_id from config params or throw error
    const garageRegisterId = config?.params?.garage_register_id;
    if (!garageRegisterId) {
      throw new Error("garage_register_id is required as a query parameter");
    }

    // Map frontend DTO to API format
    const apiData = GarageRegisterAnnouncementMapper.toCreateDto(
      data,
      garageRegisterId
    );

    // Create config with garage_register_id as query param
    const createConfig: AxiosRequestConfig = {
      ...config,
      params: {
        garage_register_id: garageRegisterId,
        ...config?.params,
      },
    };

    const response = await baseGarageRegisterAnnouncementService.create(
      apiData as Omit<GarageRegisterAnnouncementDto, "id" | "created_at" | "updated_at" | "created_by" | "garage_register">,
      createConfig
    );
    return GarageRegisterAnnouncementMapper.fromResponse(response);
  },

  update: async (
    id: string,
    data: GarageRegisterAnnouncementUpdateDto,
    config?: AxiosRequestConfig
  ): Promise<GarageRegisterAnnouncement> => {
    // Extract garage_register_id from config params
    const garageRegisterId = config?.params?.garage_register_id;
    if (!garageRegisterId) {
      throw new Error("garage_register_id is required as a query parameter");
    }

    // Map frontend DTO to API format
    const apiData = GarageRegisterAnnouncementMapper.toUpdateDto(data);

    // Create config with garage_register_id as query param
    const updateConfig: AxiosRequestConfig = {
      ...config,
      params: {
        garage_register_id: garageRegisterId,
        ...config?.params,
      },
    };

    const response = await baseGarageRegisterAnnouncementService.update(
      id,
      apiData as Partial<Omit<GarageRegisterAnnouncementDto, "id" | "created_at" | "updated_at" | "created_by" | "garage_register">>,
      updateConfig
    );
    return GarageRegisterAnnouncementMapper.fromResponse(response);
  },

  delete: async (id: string, config?: AxiosRequestConfig): Promise<void> => {
    await baseGarageRegisterAnnouncementService.delete(id, config);
  },
} as BaseService<
  GarageRegisterAnnouncement,
  GarageRegisterAnnouncementCreateDto,
  GarageRegisterAnnouncementUpdateDto
>;
